/**
 * 无后端时的数据源。
 *
 * 约束（specs/07 §7）：mock 必须与真实接口返回**同构**，UI 行为一致，
 * 这样像素对齐截图、演示与单元测试都不依赖后端是否在线。
 * 所有静态内容均来自 `src/data/`（由 `npm run sync:baseline` 从原型与黄金夹具同步），
 * 禁止在本文件里手写故事内容，避免与基线漂移。
 */

import baseline from "@/data/baseline.json";
import goldenCanon from "@/data/golden/canon.json";
import goldenChoices from "@/data/golden/choices.json";
import goldenProfile from "@/data/golden/project_profile.json";
import goldenStates from "@/data/golden/states.json";
import goldenStorylets from "@/data/golden/storylets.json";

import type {
  Artifact,
  ArtifactDiff,
  ArtifactLineage,
  AudienceAnalytics,
  AudienceMetricRow,
  AudiencePlaythroughs,
  CapabilityCatalog,
  CommunityIdea,
  CommunityPulse,
  CostSummary,
  CoverageDiffItem,
  ExportJob,
  ExportPreview,
  FollowState,
  Follows,
  Gate,
  HealthStatus,
  Methodology,
  MethodologyList,
  MyPlaythroughs,
  PathDiff,
  PlayerType,
  PlaySummary,
  Playthrough,
  ProfileImpact,
  Project,
  Json,
  ProjectAudit,
  ProjectOverview,
  Profile,
  PublishReadiness,
  Reachability,
  ReadyStatus,
  ResolvedAdapter,
  Review,
  RoleChangeLog,
  Run,
  Scheme,
  Simulation,
  SimulationCompare,
  SimulationSummary,
  Source,
  SystemMetrics,
  ValidationResult,
} from "./types";
import type { SessionUser } from "./auth";

export const DEFAULT_PROJECT_ID = "PRJ_JINLIAN_S1";

/* ---------------------------------------------------------- 账号（specs/15 §2） */

/**
 * 离线演示态（`VITE_USE_MOCK=1`）下的登录身份。
 *
 * 这是一个**编辑账号**（admin）：离线演示没有账号体系，但创作台需要身份才能进入；
 * 生产路径**不读它**——真身份来自 `GET /auth/me`（`useSession` 走 `source()` 显式开关）。
 *
 * ⚠️ `view_mode`（`ADR-025` D1）取 `editor`：离线演示的用途就是进创作台，
 * 让它停在 `player` 会使 `/account` 与头部切换器在演示态里**永远显示"观众"**——
 * 替身造不出这个身份，界面就走不到那条分支（这类不一致**不会让守卫变红**，由 `tsc` 撞出）。
 */
export const mockSessionUser: SessionUser = {
  id: "USR_mock_admin",
  email: "editor@otherwise.local",
  display_name: "离线演示编辑",
  platform_role: "editor",
  creation_role: "admin",
  view_mode: "editor",
  status: "active",
};

/** 账号列表（`GET /auth/users`，仅 admin）：账号管理的离线数据源。 */
export const mockUsers: SessionUser[] = [
  mockSessionUser,
  {
    id: "USR_mock_player",
    email: "viewer@otherwise.local",
    display_name: "离线演示玩家",
    platform_role: "player",
    creation_role: null,
    view_mode: "player",
    status: "active",
  },
];

/**
 * 角色变更记录（`GET /auth/role-changes`，`ADR-025` D4/D5）的离线数据源。
 *
 * 形状**逐字段**对齐后端 `RoleChangeLogOut`：两条不同 `action`，
 * 且**故意让 `actor_id ≠ user_id`**——离线演示也要能看出"这两种是谁做的"这条差别。
 */
export const mockRoleChangeLogs: RoleChangeLog[] = [
  {
    id: "RLOG_mock_0002",
    user_id: "USR_mock_player",
    actor_id: "USR_mock_admin",
    action: "set_role",
    from_value: "player",
    to_value: "editor",
    reason: "grant_editor:writer",
    created_at: "2026-10-04T03:20:00Z",
  },
  {
    id: "RLOG_mock_0001",
    user_id: "USR_mock_admin",
    actor_id: "USR_mock_admin",
    action: "switch_view",
    from_value: "player",
    to_value: "editor",
    reason: null,
    created_at: "2026-10-04T02:05:00Z",
  },
];

/* ------------------------------------------------------------------ 项目 */

export const mockProject: Project = {
  id: DEFAULT_PROJECT_ID,
  name: "金怜盛放 · 第一季",
  slug: "jinlian-s1",
  status: "active",
  current_stage: "S5",
  methodology_version: "latent_narrative@0.3.0-draft",
  locked_profile_id: "PROFILE_JINLIAN_S1",
  locked_profile_version: "1.0.0",
  publish_status: "published",
  is_published: true,
  published_at: "2026-07-02T09:12:00Z",
  synopsis: "一个关于边界、代价与自我保全的宋世故事。",
  // 离线演示不提供真实图片资源：如实为 null，卡片不渲染封面（D2）。
  cover_url: null,
  created_at: "2026-07-02T09:12:00Z",
};

export const mockProjects: Project[] = [
  mockProject,
  {
    id: "PRJ_NEW_UNTITLED",
    name: "新项目 · 未命名",
    slug: "untitled",
    status: "draft",
    current_stage: "S-1",
    methodology_version: "latent_narrative@0.3.0-draft",
    locked_profile_id: null,
    locked_profile_version: null,
    publish_status: "unpublished",
    is_published: false,
    published_at: null,
    synopsis: null,
    cover_url: null,
    created_at: "2026-09-01T14:03:00Z",
  },
];

/** 公开作品目录（`GET /plays`，`specs/15` §12）——与真实响应同构。 */
export const mockPlays: PlaySummary[] = [
  {
    project_id: DEFAULT_PROJECT_ID,
    name: "金怜盛放 · 第一季",
    slug: "jinlian-s1",
    genre: "ROMANCE",
    synopsis: "一个关于边界、代价与自我保全的宋世故事。",
    cover_url: null,
    published_at: "2026-07-02T09:12:00Z",
    storylet_count: 16,
    playthrough_count: 3,
    // 离线替身：给一个非零值，便于本地核对"有订阅时卡片确实渲染该行"。
    follower_count: 2,
  },
];

/** 发布就绪检查（`GET /projects/{id}/publish/readiness`）——夹具已就绪。 */
export const mockPublishReadiness: PublishReadiness = {
  project_id: DEFAULT_PROJECT_ID,
  ready: true,
  checks: [
    {
      id: "storylet",
      ok: true,
      message: "可播内容就绪：16 个 storylet",
      suggestion: "",
    },
  ],
};

/**
 * 本人进行中游玩（`GET /audience/playthroughs`，续玩 · `specs/15` §3）。
 *
 * ⚠️ 离线演示态一律返回这一条；真实路径上该端点**只返回本人**的游玩。
 * `current_storylet_title` 刻意取得与起始幕不同（起始幕是「红烛借身」），
 * 以便测试能区分"续玩块"与"播放器首幕"两处文案。
 */
export const mockMyPlaythroughs: MyPlaythroughs = {
  status: "IN_PROGRESS",
  items: [
    {
      id: "PLAY_OFFLINE_1",
      project_id: DEFAULT_PROJECT_ID,
      project_name: "金怜盛放 · 第一季",
      status: "IN_PROGRESS",
      steps_taken: 1,
      current_storylet_title: "翌日清晨",
      started_at: "2026-10-06T09:12:00Z",
    },
  ],
};

/* --------------------------------------------------------- 追更订阅（ADR-024） */

/**
 * 离线的追更订阅（与真实端点同构）。**可变**：离线订阅 / 取消要能反映在列表里，
 * 否则点一次"追更"按钮后界面不会变（那正是"离线演示必须是真闭环"的反面）。
 */
export const mockFollows: Follows = { items: [] };

/**
 * 「本周共创数据」脉冲（`GET /community/pulse`，`specs/15` §10 第 8 条）。
 *
 * ⚠️ 只有**两个**数字：另两个（"半专业整理" / "被项目采用"）没有来源，界面不显示。
 */
export const mockCommunityPulse: CommunityPulse = {
  weekly_audience: 12,
  candidate_pool: 3,
  week_start: "2026-10-05T00:00:00+00:00",
};

/** 离线订阅（幂等）：与后端同语义——重复订阅不产生第二条。 */
export function mockFollowProject(projectId: string): FollowState {
  if (!mockFollows.items.some((item) => item.project_id === projectId)) {
    const play = mockPlays.find((item) => item.project_id === projectId);
    mockFollows.items = [
      ...mockFollows.items,
      {
        project_id: projectId,
        name: play?.name ?? projectId,
        publish_status: "published",
        followed_at: new Date().toISOString(),
      },
    ];
  }
  return { project_id: projectId, following: true };
}

/** 离线取消订阅（幂等）。 */
export function mockUnfollowProject(projectId: string): FollowState {
  mockFollows.items = mockFollows.items.filter((item) => item.project_id !== projectId);
  return { project_id: projectId, following: false };
}

/* ------------------------------------------------------- 六维分类（G-1） */

// 六维分类的**定义与枚举取值**是真实产物，已迁至 `lib/taxonomy.ts`：
// 分类向导（生产路径）必须能直接引用它，而 mock 模块只允许被
// `api/queries.ts` 的显式离线模式引用（RULES §11.2）。
// 这里仅保留重导出，供离线替身与测试使用。
export type { DimensionDef, DimensionId, DimensionOption } from "@/lib/taxonomy";
export { TAXONOMY as mockTaxonomy } from "@/lib/taxonomy";

/** `/profile/resolve` 的解析结果：生产参数（与黄金夹具的真实解析输出同构）。 */
export const mockResolvedParameters: Record<string, Json> = {
  path_model: "braided_video_paths",
  choice_density: {
    per_episode: { min: 2, max: 3 },
    placement: "事件与关系转折",
    seconds_between_choices: [120, 240],
  },
  failure_policy: "continuous_watch",
  replay_policy: "追更与路径补全",
  state_visibility: "称谓、许可、站位、公开见证",
  deliverables: ["正式剧本", "分场卡", "互动 Schema", "制作资源表"],
  validators: ["R02", "R06", "R09", "R10", "R11", "R12", "R15", "R18", "R21", "R22"],
};

/** 适配器链：顺序即 ADR-008 的解析顺序。 */
export const mockAdapters: ResolvedAdapter[] = [
  {
    id: "INTERACTIVE_DRAMA",
    kind: "product_form",
    version: "0.3.0",
    label: "互动剧",
    summary: "以观看为主的连续互动作品；去掉按钮后仍必须像一部成立的剧。",
  },
  {
    id: "SOCIAL_STRATEGY",
    kind: "interaction_mode",
    version: "0.3.0",
    label: "关系策略",
    summary: "玩家通过改变他人立场、权限与舆论来推进目标。",
  },
  {
    id: "VERTICAL_INTERACTIVE_DRAMA_8MIN",
    kind: "platform",
    version: "0.3.0",
    label: "竖屏互动剧 · 8 分钟",
    summary: "移动端竖屏、单集 6-9 分钟、连载更新。",
  },
  {
    id: "ROMANCE",
    kind: "dramatic_genre",
    version: "0.3.0",
    label: "情感",
    summary: "关系变化构成主要张力，选择改变亲疏与承诺。",
  },
  {
    id: "song_dynasty_fiction",
    kind: "subject_domain",
    version: "0.3.0",
    label: "宋代（虚构清河县）",
    summary: "制度可执行性优先，专业领域需先过研究关口。",
  },
];

export const mockProfile: Profile = {
  id: "PROFILE_JINLIAN_S1",
  project_id: DEFAULT_PROJECT_ID,
  version: 1,
  status: "approved",
  content: {
    player_role: "潘金怜 · 借身者",
    product_form: { primary: goldenProfile.product_form.primary, secondary: null },
    interaction_mode: {
      primary: goldenProfile.interaction_mode.primary,
      secondary: goldenProfile.interaction_mode.secondary,
    },
    dramatic_genre: {
      primary: goldenProfile.dramatic_genre.primary,
      secondary: goldenProfile.dramatic_genre.secondary,
      tone_tags: goldenProfile.dramatic_genre.tone_tags,
    },
    subject_domain: goldenProfile.subject_domain,
    audience_profile: goldenProfile.audience_profile,
    platform_profile: goldenProfile.platform_profile,
    episode_format: goldenProfile.episode_format,
    failure_policy: goldenProfile.failure_policy,
    replay_policy: goldenProfile.replay_policy,
    production_budget_profile: goldenProfile.production_budget_profile,
    project_override: {},
    experiment_flags: { primary: null, secondary: null },
  },
  resolved: mockResolvedParameters,
  conflicts: [],
  missing: [],
  adapters: mockAdapters,
  approved_by: "usr_chen",
  approved_at: "2026-07-04T10:20:00Z",
};

export const mockSchemes: Scheme[] = [
  {
    id: "SCH_SURVIVAL",
    name: "生存优先方案",
    patch: { interaction_mode: "SURVIVAL_TRIAL", dramatic_genre: "THRILLER" },
    difference: "把社会策略改为生存试炼，压力从制度转向即时危险",
    consequence: "所有 LOCK 节点需要重写进入条件；A05 毒局将提前到 A03",
    resolution_status: "conflict",
    conflicts: [
      {
        dimensions: ["interaction_mode", "dramatic_genre"],
        rule: "C-04",
        message: "生存试炼要求资源稀缺，与已锁定的公共信用（社会型状态）冲突",
        suggestion: "改为 SURVIVAL_TRIAL + SUSPENSE，或保留 SOCIAL_STRATEGY",
      },
    ],
    impact: { rewritten_artifacts: 24, added_scenes: 6, cost_delta: 1850 },
  },
  {
    id: "SCH_INVESTIGATION",
    name: "调查线方案",
    patch: { interaction_mode: "INVESTIGATION" },
    difference: "在关系策略之外增加调查推理作为次模式",
    consequence: "需在 A03 与 A07 各增加一个信息公开节点，素材 +2 场景",
    resolution_status: "resolvable",
    conflicts: [],
    impact: { rewritten_artifacts: 8, added_scenes: 2, cost_delta: 620 },
  },
  {
    id: "SCH_MANAGEMENT",
    name: "经营治理方案",
    patch: { interaction_mode: "MANAGEMENT" },
    difference: "把市集线从验证升级为持续经营",
    consequence: "A04 之后需要常驻经营面板；预算超阈值的风险上升",
    resolution_status: "resolvable",
    conflicts: [],
    impact: { rewritten_artifacts: 12, added_scenes: 4, cost_delta: 1240 },
  },
];

/* ------------------------------------------------------------ 关口 G-1..G7 */

export const mockGates: Gate[] = [
  {
    gate: "G-1",
    title: "Profile 锁定",
    stage: "S-1",
    status: "approved",
    artifact_types: [],
    approval_entry: "profile",
    approved_by: "usr_chen",
    approved_by_role: "project_owner",
    approved_at: "2026-07-04T10:20:00Z",
    comment: "六维分类确认，进入 Brief 阶段。",
    exception_reason: "",
    rejections: [],
  },
  {
    gate: "G0",
    title: "Brief 批准",
    stage: "S0",
    status: "approved",
    artifact_types: ["brief"],
    approval_entry: "gate",
    approved_by: "usr_chen",
    approved_by_role: "project_owner",
    approved_at: "2026-07-06T15:04:00Z",
    comment: "改编边界与“不降智”底线已写入 Brief。",
    exception_reason: "",
    rejections: [],
  },
  {
    gate: "G1",
    title: "创作方向批准",
    stage: "S1",
    status: "approved",
    artifact_types: ["theme"],
    approval_entry: "gate",
    approved_by: "usr_mo",
    approved_by_role: "head_writer",
    approved_at: "2026-07-11T11:31:00Z",
    comment: "创作立场：不在古代复刻现代爽感。",
    exception_reason: "",
    rejections: [],
  },
  {
    gate: "G2",
    title: "Canon 锁定",
    stage: "S2",
    status: "approved",
    artifact_types: ["canon", "character", "relationship", "state"],
    approval_entry: "gate",
    approved_by: "usr_chen",
    approved_by_role: "project_owner",
    approved_at: "2026-07-15T09:48:00Z",
    comment: "人物底线与状态字典 v0.2.0 通过。",
    exception_reason: "",
    rejections: [],
  },
  {
    gate: "G3",
    title: "结构锁定",
    stage: "S3",
    status: "approved",
    artifact_types: ["anchor"],
    approval_entry: "gate",
    approved_by: "usr_mo",
    approved_by_role: "head_writer",
    approved_at: "2026-07-22T17:05:00Z",
    comment: "九锚点 + 六锁点主干不再变动。",
    exception_reason: "",
    rejections: [],
  },
  {
    gate: "G4",
    title: "互动锁定",
    stage: "S4",
    status: "approved",
    artifact_types: ["storylet", "choice"],
    approval_entry: "gate",
    approved_by: "usr_mo",
    approved_by_role: "head_writer",
    approved_at: "2026-08-02T14:22:00Z",
    comment: "Storylet 池与选择规则通过，可进入分场。",
    exception_reason: "",
    rejections: [],
  },
  {
    gate: "G5",
    title: "分场锁定",
    stage: "S5",
    status: "pending",
    artifact_types: ["scene"],
    approval_entry: "gate",
    approved_by: null,
    approved_by_role: null,
    approved_at: null,
    comment: "",
    exception_reason: "",
    rejections: [
      {
        at: "2026-08-28T10:02:00Z",
        actor: "usr_wu",
        role: "producer",
        reason: "市集线新增 6 个独立场景，超过素材倍数阈值（R07）",
      },
    ],
  },
  {
    gate: "G6",
    title: "剧本批准",
    stage: "S6",
    status: "pending",
    artifact_types: ["script"],
    approval_entry: "gate",
    approved_by: null,
    approved_by_role: null,
    approved_at: null,
    comment: "",
    exception_reason: "",
    rejections: [],
  },
  {
    gate: "G7",
    title: "生产就绪",
    stage: "S7",
    status: "pending",
    artifact_types: ["test_report"],
    approval_entry: "gate",
    approved_by: null,
    approved_by_role: null,
    approved_at: null,
    comment: "",
    exception_reason: "",
    rejections: [],
  },
  {
    // P-1 上架审核（`ADR-022`）：不是阶段关口（`stage = null`），入口在发布面板。
    gate: "P-1",
    title: "上架审核",
    stage: null,
    status: "approved",
    artifact_types: [],
    approval_entry: "publish",
    approved_by: "usr_chen",
    approved_by_role: "head_writer",
    approved_at: "2026-07-10T09:00:00Z",
    comment: "可播内容就绪，批准上架。",
    exception_reason: "",
    rejections: [],
  },
];

/* ------------------------------------------------------------- 校验与模拟 */

export const mockValidation: ValidationResult = {
  issues: [
    {
      rule_id: "R03",
      severity: "error",
      artifact_id: "ART_STATE_001",
      field_path: "content.EVIDENCE_CHAIN.visibility",
      message: "信息型状态 EVIDENCE_CHAIN 既未公开可读，也未登记知识标记",
      suggestion: "将其 visibility 改为 surfaced，或在关系的 knowledge_flags 中登记持有者",
    },
    {
      rule_id: "R07",
      severity: "warning",
      artifact_id: "ART_SCENE_A04_02",
      field_path: "content.asset_level",
      message: "市集线独立场景数超出预算阈值，预计有效素材倍数 2.9×",
      suggestion: "合并 3 个市集场景为共享场景 + 变体",
    },
    {
      rule_id: "R02",
      severity: "warning",
      artifact_id: "ART_CH_A02_01",
      field_path: "content.options[2].state_delta",
      message: "选项“账目换缓冲”仅改变态度，未改变状态、内容或未来成本",
      suggestion: "补一条 immediate_cost：武值戒心上升，A03 议价空间缩小",
    },
    {
      rule_id: "R04",
      severity: "warning",
      artifact_id: "ART_ST_A03",
      field_path: "content.required_residue",
      message: "A01 的“指腹针茧”承诺尚未在 A03 之前回收",
      suggestion: "在 A02 或 A03 增加一次可被玩家察觉的回响",
    },
  ],
  summary: {
    blocking: 1,
    warnings: 3,
    rules_run: 22,
    checks_run: 8,
    score: 78,
    passed: false,
  },
  methodology_version: mockProject.methodology_version,
  profile_version: "1.0.0",
};

/**
 * 一次模拟**默认覆盖**的六类玩家（`SAVE_SCUMMER` 只在目录里可选，不进默认跑批）。
 * 只留 id：中文短名由视图自己的表（`SimulateView`）提供，本文件不再维护第二份标签。
 */
const RUN_PLAYER_TYPES = [
  "SAFE_SURVIVAL",
  "RELATION_FIRST",
  "POWER_FIRST",
  "RISK_EXPLORER",
  "MORAL_ABSOLUTE",
  "MIN_INFO",
];

/**
 * `GET /player-types` 的**离线副本**。
 *
 * ⚠️ 与后端 `PLAYER_TYPES` **逐条对齐**：7 条（含实验性的 `SAVE_SCUMMER`），
 * `description` 是**完整策略描述**而不是中文短名——真实响应（`player_catalog()`）
 * 返回的就是 `{id, description}`，没有 `label` 字段。
 * 由 `make guard` 的 `catalog-contract` 双向对表守着（RULES §29）。
 */
export const mockPlayerTypes: PlayerType[] = [
  { id: "SAFE_SURVIVAL", description: "求稳生存：优先降低即时风险，回避不可逆承诺" },
  { id: "RELATION_FIRST", description: "关系优先：优先提升信任与亲密，接受资源损失" },
  { id: "POWER_FIRST", description: "事业/权力优先：优先提升资源、权限与治理控制" },
  { id: "RISK_EXPLORER", description: "高风险探索：优先未知信息，接受债务与暴露" },
  { id: "MORAL_ABSOLUTE", description: "道德绝对主义：拒绝伤害他人、拒绝欺骗与越界" },
  { id: "MIN_INFO", description: "信息最少路径：只使用已公开信息，不做额外探索" },
  { id: "SAVE_SCUMMER", description: "读档最优解：反复重玩寻找最优结果（实验性）" },
];

/** 黄金夹具里 storylet / choice 的最小形状（mock 派生用；**不新写故事内容**）。 */
interface MockStoryletShape {
  id: string;
  title: string;
  choice_id?: string | null;
  entry_effects?: Record<string, number>;
}

interface MockChoiceShape {
  id: string;
  options?: { id: string; state_delta?: Record<string, number> }[];
}

/**
 * 构造一条玩家路径。
 *
 * **2026-09-14 按真机形态重写**：真机（`PRJ_JINLIAN_S1`）六条玩家路径的步骤是
 * **一样**的——先把 10 个非结局 Storylet 顺序走完，再落到本条路径自己的那个结局；
 * 六类玩家的分歧只体现在**落到哪个结局**上。
 *
 * 此前用 `goldenStorylets.storylets.slice(0, N)` 取前缀，导致长路径（N=14）的步骤里
 * 同时出现 `END_01`…`END_04` 四个结局节点——一条路径不可能路过四个结局，
 * 这是真机永远不会出现的形态。现在改成"非结局 + 自己的结局"，同时 `failures`
 * 也回到真机的空数组（真机失败项形状是 `{kind, storylet_id, detail, rule_id}`，
 * 此前 mock 里写的 `{rule_id, severity, at, message}` 后端从不产生）。
 *
 * **2026-10-04 追加**：步骤形状补齐为真机的 `{…, choice_id, option_id, delta, state}`
 * （`specs/07`「图谱与路径补全」的路径回放消费），全部由黄金夹具**派生**。
 */
function playerPath(
  playerType: string,
  endingId: string,
  endingExplanation: string,
): Simulation["paths"][number] {
  const walk = goldenStorylets.storylets.filter(
    (storylet) => !(storylet.type ?? []).some((type) => String(type).toUpperCase() === "ENDING"),
  );
  const ending = goldenStorylets.storylets.find((storylet) => storylet.id === endingId);

  /*
   * 步骤形状与真机对齐（`simulator.Step.as_dict()`）：
   * `{storylet_id, title, choice_id, option_id, delta, state}`。
   * delta = 场景 `entry_effects` + 该选择首个选项的 `state_delta`（与 `simulator.run_path`
   * 同一口径）；`state` 为逐步累积快照。全部**从黄金夹具派生**，不手写故事内容。
   */
  const timeline = [...walk, ...(ending ? [ending] : [])] as unknown as MockStoryletShape[];
  const choiceById = new Map(
    ((goldenChoices as unknown as { choices?: MockChoiceShape[] }).choices ?? []).map((choice) => [
      choice.id,
      choice,
    ]),
  );
  const running: Record<string, number> = {
    ...((goldenStates as unknown as { initial_state?: Record<string, number> }).initial_state ??
      {}),
  };
  const steps = timeline.map((storylet, index) => {
    const delta: Record<string, number> = { ...(storylet.entry_effects ?? {}) };
    const choiceId = storylet.choice_id ?? null;
    const option = (choiceId ? (choiceById.get(choiceId)?.options ?? []) : [])[0];
    const optionId = option?.id ?? null;
    Object.assign(delta, option?.state_delta ?? {});
    for (const [key, value] of Object.entries(delta)) {
      running[key] = (running[key] ?? 0) + value;
    }
    return {
      storylet_id: storylet.id,
      title: storylet.title,
      choice_id: choiceId,
      option_id: optionId,
      delta,
      state: { ...running },
      order: index + 1,
    };
  });

  return {
    player_type: playerType,
    mode: "player",
    steps,
    ending_id: endingId,
    ending_explanation: endingExplanation,
    failures: [],
    unpaid_debts: [],
    absent_characters: [],
    // PR-141：决策原因（mock 简化：每类玩家给 2 条有代表性的原因，不代表每步都有一条）
    choices_made: [
      `player=${playerType}`,
      `${playerType === "SAFE_SURVIVAL" ? "risk" : "affinity"}=${playerType === "SAFE_SURVIVAL" ? 0.2 : 3.0}`,
    ],
    // PR-151：资产增量（5 键固定，自洽小数字）
    asset_increments: {
      new_scenes: 3,
      new_characters: 2,
      new_wardrobe: 1,
      new_props: 0,
      new_shots: 1,
    },
    // PR-152：制作等级分布（4 键固定）
    production_class_summary: { P0: 2, P1: 2, P2: 1, P3: 0 },
    // PR-152：预算告警。mock 的离线模拟无告警。
    // ⚠️ 即使有告警，UI 也只经 issues（R13）呈现，不在此重复消费（去重决策）。
    budget_warnings: [],
  };
}

/**
 * 六路径模拟快照（`POST /projects/{id}/simulations` 的落库形态）。
 *
 * **2026-09-14 按真机重写**：`coverage` 与 `paths` 全部取自 `PRJ_JINLIAN_S1` 的
 * 真实响应（真实 6 条玩家路径：SAFE_SURVIVAL/RELATION_FIRST/MIN_INFO → END_04、
 * POWER_FIRST/RISK_EXPLORER → END_06、MORAL_ABSOLUTE → END_02）。
 *
 * 两条此前一直没人发现的错：
 * 1. `ending_coverage` 是**编的**——6 条路径却有 `{3,5,9,2,4,6}`（求和 29）。
 *    真机不变式是 **`sum(ending_coverage) === paths`**（每个路径只落一个结局），
 *    且**必须列出全部 6 个结局节点**（未达的记 0，不是省略）。真实取值求和 = 6。
 * 2. `storylet_coverage` 0.8125 与 `endings_reachable_ratio` 1.0 **互相矛盾**：
 *    0.8125 = 13/16，而 16 个 Storylet 里有 6 个是结局节点——只有**恰好 3 个结局
 *    可达**（visited 才会恰好是 10 个非结局 + 3 个结局 = 13）时它才成立，
 *    对应 `endings_reachable_ratio` = 3/6 = **0.5**。
 *    真机值正是 0.8125 / visited 13 项 / ratio 0.5，三者自洽。
 *
 * `status` 与 `issues` 同样按真机：模拟 JSON 里的 issue **只**由路径失败项推导，
 * 真机 6 条路径均无失败 → `issues` 为空 → 状态为 `SUCCEEDED`
 * （`simulation_service` 写的是 `"SUCCEEDED" if not issues else "WITH_ISSUES"`）。
 */
export const mockSimulation: Simulation = {
  id: "SIM_000918",
  project_id: DEFAULT_PROJECT_ID,
  status: "SUCCEEDED",
  player_types: [...RUN_PLAYER_TYPES],
  paths: [
    playerPath("SAFE_SURVIVAL", "END_04", "由前史累积达成：女性盟友=3（要求 >= 3）。"),
    playerPath("RELATION_FIRST", "END_04", "由前史累积达成：女性盟友=3（要求 >= 3）。"),
    playerPath(
      "POWER_FIRST",
      "END_06",
      "由前史累积达成：控制倾向=7（要求 >= 6）、劳动债=3（要求 >= 2）。",
    ),
    playerPath(
      "RISK_EXPLORER",
      "END_06",
      "由前史累积达成：控制倾向=7（要求 >= 6）、劳动债=2（要求 >= 2）。",
    ),
    playerPath("MORAL_ABSOLUTE", "END_02", "由前史累积达成：证据链=4（要求 >= 4）。"),
    playerPath("MIN_INFO", "END_04", "由前史累积达成：女性盟友=3（要求 >= 3）。"),
  ],
  coverage: {
    storylet_coverage: 0.8125,
    choice_coverage: 1,
    // §19.2 重大选择覆盖率：锁点选择 4 个（CH_A02/A05/A07/A09_01）、每节点 3 选项 → 分母 12；
    // 六条路径在 mock 里都取首选项 → 分子 4。派生自同一份黄金夹具，不另造数字。
    anchor_lock_options_total: 12,
    anchor_lock_options_used: 4,
    anchor_lock_choice_coverage: 0.3333,
    ending_coverage: { END_01: 0, END_02: 1, END_03: 0, END_04: 3, END_05: 0, END_06: 2 },
    endings_reached: ["END_02", "END_04", "END_06"],
    endings_total: 6,
    endings_reachable_ratio: 0.5,
    paths: 6,
    failed_paths: 0,
    dead_ends: 0,
    romance_free_paths: 4,
    visited: [
      "A01",
      "A02",
      "A03",
      "A04",
      "A05",
      "A06",
      "A07",
      "A08",
      "A09",
      "END_02",
      "END_04",
      "END_06",
      "ST_0203_01",
    ],
  },
  issues: [],
  methodology_version: mockProject.methodology_version,
  profile_version: "1.0.0",
};

/**
 * 结局可达性（`GET /projects/{id}/simulations/reachability`）。
 *
 * **2026-09-14 修正一处被长期混同的事实**：此前 `coverage` 直接复用
 * `mockSimulation.coverage`——但可达性走的是 `enumerate_paths`，**枚举全部组合**
 * （真机 243 条），与只跑 6 类玩家的模拟是**两套完全不同的数字**：
 * 可达性 243 条 → `storylet_coverage` 1.0、6 个结局全可达；模拟 6 条 → 0.8125、3/6。
 * 同理 `combinations` 与 `coverage.paths` 在真机里**恒等**（都是 `len(combos)`）。
 *
 * ⚠️ 唯一保留的简化：`paths` 只内联 6 条玩家路径作样例，不内联真机的 243 条；
 * 消费方（`SimulateView`）只用 `combinations`。`coverage` 与 `combinations` 均为真机值。
 */
export const mockReachability: Reachability = {
  coverage: {
    storylet_coverage: 1,
    choice_coverage: 1,
    // 枚举全组合：4 个锁点 × 3 选项 = 12 全部被走到 → 覆盖率 1。
    anchor_lock_options_total: 12,
    anchor_lock_options_used: 12,
    anchor_lock_choice_coverage: 1,
    ending_coverage: { END_01: 30, END_02: 81, END_03: 12, END_04: 30, END_05: 63, END_06: 27 },
    endings_reached: ["END_01", "END_02", "END_03", "END_04", "END_05", "END_06"],
    endings_total: 6,
    endings_reachable_ratio: 1,
    paths: 243,
    failed_paths: 0,
    dead_ends: 0,
    romance_free_paths: 162,
    visited: [
      "A01",
      "A02",
      "A03",
      "A04",
      "A05",
      "A06",
      "A07",
      "A08",
      "A09",
      "END_01",
      "END_02",
      "END_03",
      "END_04",
      "END_05",
      "END_06",
      "ST_0203_01",
    ],
  },
  paths: mockSimulation.paths,
  combinations: 243,
};

/**
 * 模拟历史（`GET /projects/{id}/simulations`，F-5）。
 *
 * 两条记录，且**各自内部自洽**——这是 2026-09-14 那轮的教训：mock 里曾出现
 * `ending_coverage` 求和 29 而 `paths` 只有 6 这类"每个数字单看都像真的、
 * 合起来不可能"的伪造。所以这里刻意让第二条（带告警那次）满足同一条硬不变式：
 * `sum(ending_coverage) === paths_count === 6`。
 *
 * 第一条直接用 `mockSimulation` 派生，不另写一份数字——两份真相必然漂移。
 */
export const mockSimulations: SimulationSummary[] = [
  {
    id: mockSimulation.id,
    project_id: mockSimulation.project_id,
    status: mockSimulation.status,
    player_types: mockSimulation.player_types,
    coverage: mockSimulation.coverage,
    issues_count: mockSimulation.issues.length,
    paths_count: mockSimulation.paths.length,
    methodology_version: mockSimulation.methodology_version,
    profile_version: mockSimulation.profile_version,
    created_at: "2026-09-14T09:30:00+00:00",
  },
  {
    id: "SIM_JINLIAN_EARLIER",
    project_id: mockSimulation.project_id,
    status: "WITH_ISSUES",
    player_types: mockSimulation.player_types,
    coverage: {
      storylet_coverage: 0.75,
      choice_coverage: 1,
      ending_coverage: { END_01: 0, END_02: 3, END_03: 0, END_04: 2, END_05: 0, END_06: 1 },
      endings_reached: ["END_02", "END_04", "END_06"],
      endings_total: 6,
      endings_reachable_ratio: 0.5,
      paths: 6,
      failed_paths: 1,
      dead_ends: 1,
      romance_free_paths: 3,
      visited: [
        "A01",
        "A02",
        "A03",
        "A04",
        "A05",
        "A06",
        "A07",
        "A08",
        "A09",
        "ST_0203_01",
        "END_02",
        "END_04",
      ],
    },
    // 1 条告警必须对应 1 条失败路径：模拟的 issues 只由路径失败项推导
    issues_count: 1,
    paths_count: 6,
    methodology_version: mockSimulation.methodology_version,
    profile_version: mockSimulation.profile_version,
    created_at: "2026-09-13T15:02:00+00:00",
  },
];

/**
 * 审计日志（`GET /projects/{id}/audit`）。
 *
 * `counts` 与 `events` 在本文件里**数量一致**（离线不截断），因此可以由
 * `events` 直接数出来——这正是测试要断言的：mock 不许自相矛盾。
 */
export const mockProjectAudit: ProjectAudit = {
  project_id: mockProject.id,
  generated_at: "2026-09-14T09:31:00+00:00",
  counts: { runs: 1, issues: 1, decisions: 3 },
  events: [
    {
      kind: "decision",
      at: "2026-09-14T09:28:00+00:00",
      event_id: "REV_JINLIAN_G5",
      title: "approved · G5",
      detail: "分场结构与状态字典一致，同意过关",
      status: "approved",
      rule_id: null,
      artifact_id: "ART_SCENE_JINLIAN_01",
      actor_id: "usr_chen",
      actor_role: "head_writer",
    },
    {
      kind: "issue",
      at: "2026-09-14T09:12:00+00:00",
      event_id: "VIS_JINLIAN_R11",
      title: "R11",
      detail: "END_05 在全部锁点组合中均不可达，需要补一条进入路径",
      status: "error",
      rule_id: "R11",
      artifact_id: "ART_STORYLET_JINLIAN_01",
      actor_id: null,
      actor_role: null,
    },
    {
      kind: "run",
      at: "2026-09-14T09:10:00+00:00",
      event_id: "RUN_JINLIAN_STRUCT",
      title: "structure_engineer",
      detail: "run_key=structure:jinlian-s1:2026-09-14",
      // 取值来自 `capability/executor.py` 的工作流状态机，**不是** SUCCEEDED/FAILED：
      // 那两位在真实后端不存在（见 StatusLabel.RunStateLabel 的说明）。
      status: "APPROVED",
      rule_id: null,
      artifact_id: null,
      actor_id: null,
      actor_role: null,
    },
    {
      kind: "decision",
      at: "2026-09-13T15:40:00+00:00",
      event_id: "REV_JINLIAN_G4",
      title: "approved · G4",
      detail: "黄金夹具初始化",
      status: "approved",
      rule_id: null,
      artifact_id: "ART_STORYLET_JINLIAN_01",
      actor_id: "usr_chen",
      actor_role: "head_writer",
    },
    {
      kind: "decision",
      at: "2026-09-13T11:05:00+00:00",
      event_id: "REV_JINLIAN_G2",
      title: "rejected · G2",
      detail: "真相层里「六年」与人物年龄对不上，先改再送审",
      status: "rejected",
      rule_id: null,
      artifact_id: "ART_CANON_JINLIAN_06",
      actor_id: "usr_lin",
      actor_role: "editor",
    },
  ],
};

/* --------------------------------------------------------- 生成中心 / 成本 */

export const mockGenerationTasks = baseline.generationTasks;

export const mockCost: CostSummary = {
  used: 3580,
  quota: 10000,
  remaining: 6420,
  ratio: 0.358,
  org_used: 18420,
  org_quota: 40000,
  by_model: [
    { model: "方法模型 · 对白", units: 438, calls: 26 },
    { model: "FrameSeed C2", units: 1220, calls: 14 },
    { model: "VideoFlow S", units: 1922, calls: 9 },
  ],
  generated_at: "2026-09-05T10:42:00Z",
};

/**
 * `mockRuns` 用到的那两个能力的**标签**。真实后端里标签来自 `GET /capabilities`。
 *
 * 这里必须与 `mockCapabilities` 保持一致，而这份同步**由测试断言守护**
 * （`lifecycle-entries.test.tsx` 里比对 `run.title` 的前缀），不靠人工比对。
 *
 * 之所以不直接引用 `mockCapabilities`：`mockRuns` 在模块求值时就需要标签，
 * 而 `mockCapabilities` 定义在本文件后半，直接引用会命中 TDZ。
 */
const MOCK_RUN_CAPABILITY_LABELS = {
  writer_assistant: "编剧助理",
  playtester: "测试玩家",
} as const;

/**
 * 一次运行的**生效写入目标**，与后端同构：未显式指定时回落
 * `sorted(capability.ALLOWED_WRITE)[0]`（`writer_assistant` → `brief`，`playtester` → `test_report`）。
 */
function mockRunWriteTarget(taskType: string): string {
  return taskType === "text" ? "brief" : "test_report";
}

/** 兜底标题，与后端 `f"{capability.label}候选 · {task[:40]}"` 同形。 */
function mockRunTitle(taskType: string, taskName: string): string {
  const label =
    taskType === "text"
      ? MOCK_RUN_CAPABILITY_LABELS.writer_assistant
      : MOCK_RUN_CAPABILITY_LABELS.playtester;
  return `${label}候选 · ${taskName.slice(0, 40)}`;
}

export const mockRuns: Run[] = baseline.generationTasks.map((task, index) => ({
  id: `RUN_${1200 + index}`,
  project_id: DEFAULT_PROJECT_ID,
  // 能力 id 必须与 `GET /capabilities` 一致（早期用的 content_director 不存在）。
  capability: task.type === "text" ? "writer_assistant" : "playtester",
  // 状态取值见 `RunStatus`；SUCCEEDED / FAILED 在真实后端不存在。
  status:
    task.status === "approved" ? "APPROVED" : task.status === "failed" ? "BLOCKED" : "RUNNING",
  run_key: `${task.id}-v1`,
  // 请求元数据各有其位（ADR-015）：不再往 `inputs` 里塞 `task` / `artifact_ids`。
  task: task.name,
  artifact_ids: [task.source],
  // 写入元数据各有其位（ADR-016）：不暴露它们，就无法判断"重试是否写到同一个地方"。
  // 取值与后端**同构**：写入目标由能力模块的默认目标决定（ADR-017，`DEFAULT_WRITE_TARGET`，
  // mock 层用 `mockRunWriteTarget` 按 task 类型给展示默认值），逻辑键是 `f"{write_target}:{run.id}"`。
  write_target: mockRunWriteTarget(task.type),
  logical_key: `${mockRunWriteTarget(task.type)}:RUN_${1200 + index}`,
  title: mockRunTitle(task.type, task.name),
  // 只放调用方自由输入的参数。
  inputs: { source: task.source },
  outputs: {},
  issues: [],
  methodology_version: mockProject.methodology_version,
  profile_version: "1.0.0",
  model: task.model,
  // 离线演示模式下**没有任何模型被调用过**，所以照实写 `"fake"`（= `FakeProvider.name`），
  // 不写 `null`、更不写真实协议名。这同时满足 RULES §11.2 的要求：
  // 显式演示模式必须在界面上**持续可见**地标注演示态——运行详情与生成列表都会据此标注。
  // 真实后端在同一位置给的是"这次产出由谁生成"，取值域与此相同（fake / http / responses）。
  provider: "fake",
  context_chars: 4200 + index * 380,
  cost_units: task.cost,
  prompt_tokens: 1800 + index * 120,
  completion_tokens: 640 + index * 90,
  latency_ms: 2400 + index * 310,
  error: task.status === "failed" ? "上游模型超时（已重试 2 次）" : "",
  retry_count: task.status === "failed" ? 2 : 0,
  created_at: "2026-09-05T09:10:00Z",
  finished_at: task.status === "queued" ? null : "2026-09-05T09:11:30Z",
}));

/* ------------------------------------------------------------- 真相层 / 产物 */

type GoldenCanon = typeof goldenCanon;
type GoldenStates = typeof goldenStates;

export const mockCanon = goldenCanon as unknown as GoldenCanon;
export const mockStateDictionary = goldenStates as unknown as GoldenStates;

export const mockArtifacts: Artifact[] = [
  {
    id: "ART_CANON_001",
    project_id: DEFAULT_PROJECT_ID,
    type: "canon",
    logical_key: "canon/main",
    version: 3,
    title: "Canon · 金怜盛放",
    schema_version: "0.1.0",
    profile_version: "1.0.0",
    methodology_version: mockProject.methodology_version,
    status: "approved",
    gate: "G2",
    content: goldenCanon as unknown as Record<string, never>,
    parent_artifact_ids: [],
    source_refs: ["SRC_SHUIHU", "SRC_SONG_System"],
    review_record_ids: ["REV_011", "REV_012"],
    created_by: "human",
    capability_run_id: null,
    checksum: "sha256:9f2c…a17",
    stale_reason: null,
    created_at: "2026-07-14T10:00:00Z",
    updated_at: "2026-07-15T09:48:00Z",
  },
  {
    id: "ART_STATE_001",
    project_id: DEFAULT_PROJECT_ID,
    type: "state",
    logical_key: "state/dictionary",
    version: 2,
    title: "状态字典 v0.2.0",
    schema_version: "0.2.0",
    profile_version: "1.0.0",
    methodology_version: mockProject.methodology_version,
    status: "approved",
    gate: "G2",
    content: goldenStates as unknown as Record<string, never>,
    parent_artifact_ids: ["ART_CANON_001"],
    source_refs: [],
    review_record_ids: ["REV_013"],
    created_by: "human",
    capability_run_id: null,
    checksum: "sha256:3ab1…7c0",
    stale_reason: null,
    created_at: "2026-07-15T08:30:00Z",
    updated_at: "2026-09-05T13:12:00Z",
  },
];

/* ------------------------------------------------- Artifact 生命周期（只读） */

/**
 * Artifact 详情面板的离线数据（specs/14 §4.5 / 需求 §12.3）。
 *
 * 全部从既有 `mockArtifacts` **派生**，不新写故事内容（本文件顶部约束）。
 * 真实后端中每个 `logical_key` 目前也只有一版，所以这里刻意保持「一版」，
 * 让离线形态与真机同构；跨产物对比（canon ↔ state）仍可演示字段级差异。
 */

/** `GET /artifacts/{id}/versions`：同一 logical_key 的版本链（升序）。 */
export function mockArtifactVersionsFor(artifactId: string): Artifact[] {
  const target = mockArtifacts.find((item) => item.id === artifactId);
  if (!target) return [];
  return mockArtifacts
    .filter((item) => item.logical_key === target.logical_key)
    .sort((a, b) => a.version - b.version);
}

/** 评审轨迹的决策序列：由终态反推，保证与 `status` 自洽。 */
const REVIEW_TRAIL: Record<string, string[]> = {
  draft: [],
  submitted: ["submitted"],
  approved: ["submitted", "approved"],
  rejected: ["submitted", "rejected"],
  stale: ["submitted", "approved", "stale"],
  superseded: ["submitted", "approved", "superseded"],
};

/** `GET /artifacts/{id}/reviews`。 */
export function mockArtifactReviewsFor(artifactId: string): Review[] {
  const target = mockArtifacts.find((item) => item.id === artifactId);
  if (!target) return [];
  const decisions = REVIEW_TRAIL[target.status] ?? [];
  return decisions.map((decision, index) => ({
    id: target.review_record_ids[index] ?? `${target.id}_REV_${index + 1}`,
    artifact_id: target.id,
    gate: target.gate,
    decision,
    actor_id: "usr_chen",
    actor_role: "head_writer",
    comment: "",
    exception_reason: "",
    created_at: target.created_at,
  }));
}

/** `GET /artifacts/{id}/diff?against={otherArtifactId}`：字段级差异。 */
export function mockArtifactDiffFor(artifactId: string, againstId: string): ArtifactDiff | null {
  const current = mockArtifacts.find((item) => item.id === artifactId);
  const baseline = mockArtifacts.find((item) => item.id === againstId);
  if (!current || !baseline) return null;
  const before = baseline.content ?? {};
  const after = current.content ?? {};
  const changes = [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .sort()
    .filter((field) => before[field] !== after[field])
    .map((field) => ({ field, from: before[field] ?? null, to: after[field] ?? null }));
  return {
    artifact_id: current.id,
    against: baseline.id,
    changes,
    identical: changes.length === 0,
    checksums: { current: current.checksum, against: baseline.checksum },
  };
}

/** `GET /artifacts/{id}/lineage`：来源、父版本、下游与人工决定。 */
export function mockArtifactLineageFor(artifactId: string): ArtifactLineage | null {
  const target = mockArtifacts.find((item) => item.id === artifactId);
  if (!target) return null;
  const summarise = (item: Artifact) => ({
    id: item.id,
    type: item.type,
    version: item.version,
    status: item.status,
  });
  return {
    artifact: target as unknown as Record<string, Json>,
    parents: mockArtifacts
      .filter((item) => target.parent_artifact_ids.includes(item.id))
      .map(summarise) as unknown as Record<string, Json>[],
    // 真机 `lineage.children` 直接取自 `artifact_edges`，每项只有 `{id, edge_type}`；
    // 早期离线数据写成了父版本那套 `{id,type,version,status}`，两边不同构会误导界面。
    children: mockArtifacts
      .filter((item) => item.parent_artifact_ids.includes(target.id))
      .map((item) => ({ id: item.id, edge_type: "derives" })) as unknown as Record<string, Json>[],
    sources: target.source_refs,
    capability_run_id: target.capability_run_id,
    reviews: mockArtifactReviewsFor(target.id) as unknown as Record<string, Json>[],
  };
}

export const mockSources: Source[] = [
  {
    id: "SRC_SHUIHU",
    project_id: DEFAULT_PROJECT_ID,
    kind: "document",
    title: "《水浒传》相关章回（公共领域）",
    uri: "library://shuihu/chapters-23-26",
    checksum: "sha256:…",
    rights_status: "public_domain",
    meta: { note: "仅作改编基准，不复制原文" },
    created_at: "2026-07-03T11:00:00Z",
  },
  {
    id: "SRC_SONG_System",
    project_id: DEFAULT_PROJECT_ID,
    kind: "research",
    title: "宋代市集与行会制度研究笔记",
    uri: "research://song-market-guild",
    checksum: null,
    rights_status: "internal",
    meta: { research_gates: ["制度可执行性", "物质细节"] },
    created_at: "2026-07-05T16:20:00Z",
  },
];

export const mockMethodology: Methodology = {
  name: "latent_narrative",
  version: "0.3.0-draft",
  status: "draft",
  resolution_order: [
    "INVARIANT",
    "product_form",
    "interaction_mode",
    "platform_profile",
    "dramatic_genre",
    "subject_domain",
    "project_override",
    "experiment_flags",
  ],
  enums: {
    product_form: ["INTERACTIVE_FILM_GAME", "INTERACTIVE_DRAMA", "HYBRID"],
    interaction_mode: [
      "SURVIVAL_TRIAL",
      "SOCIAL_STRATEGY",
      "RELATIONSHIP_STRATEGY",
      "INVESTIGATION",
      "MANAGEMENT",
      "ETHICAL_DECISION",
    ],
    dramatic_genre: ["SUSPENSE", "ROMANCE", "COMEDY", "REVENGE", "THRILLER", "FAMILY", "WORKPLACE"],
    audience_profile: [
      "WOMEN_18_34",
      "URBAN_WOMEN_22_34",
      "MEN_18_34",
      "GENERAL_18_45",
      "TEEN_15_20",
    ],
    platform_profile: [
      "VERTICAL_VIDEO",
      "VERTICAL_INTERACTIVE_DRAMA_8MIN",
      "HORIZONTAL_LONG_EPISODE",
      "MOBILE_SERIAL",
    ],
  },
  rules: [],
  adapters: {},
  schemas: [],
  prompts: [],
  changelog: "0.3.0-draft：新增 subject_domain 研究关口与 ending_priority 字段。",
};

/* ----------------------------------------------------------------- 导出 */

/**
 * 导出任务状态取自 `app/services/exporter.py`：初始 `QUEUED`，成功写 `SUCCEEDED`。
 *
 * **2026-09-14 修正**：原先两条都写 `status: "ready"`。真实后端没有 `ready` 这个取值，
 * 于是离线演示里已完成的导出会被一律渲染成「生成中」。
 */
export const mockExports: ExportJob[] = [
  {
    id: "EXP_0042",
    project_id: DEFAULT_PROJECT_ID,
    format: "docx",
    status: "SUCCEEDED",
    artifact_ids: mockArtifacts.map((item) => item.id),
    result_uri: "/exports/金怜盛放-第一季-v0.2.6.docx",
    size_bytes: 486_400,
    created_at: "2026-09-04T18:22:00Z",
  },
  {
    id: "EXP_0041",
    project_id: DEFAULT_PROJECT_ID,
    format: "markdown",
    status: "SUCCEEDED",
    artifact_ids: mockArtifacts.map((item) => item.id),
    result_uri: "/exports/金怜盛放-第一季-v0.2.6.md",
    size_bytes: 92_180,
    created_at: "2026-09-04T18:20:00Z",
  },
  {
    // 进行中的任务：离线模式下也能覆盖「轮询到终态」这条路径。
    id: "EXP_0043",
    project_id: DEFAULT_PROJECT_ID,
    format: "yaml",
    status: "QUEUED",
    artifact_ids: [],
    result_uri: "",
    size_bytes: 0,
    created_at: "2026-09-05T10:44:00Z",
  },
];

/**
 * 导出前计数预览 —— 与 `GET /projects/{id}/exports/preview` 同构（8 个计数字段）。
 *
 * **2026-09-14 修正**：原先是 `{project_id, generated_at, sections, warnings}`，
 * 与真实响应完全不符。它既是无类型注解的对象字面量、又从未被 `useExportPreview` 消费，
 * 所以 TS 和运行时都没能发现——同 P-1401 病根。
 */
export const mockExportPreview: ExportPreview = {
  artifacts: 7,
  approved: 7,
  draft: 0,
  stale: 0,
  storylets: 16,
  choices: 5,
  scenes: 5,
  states: 14,
};

/* --------------------------------------------------------------- 总览 */

export const mockOverview: ProjectOverview = {
  project: mockProject,
  health: { score: 78, errors: 2, warnings: 6, blocking: true },
  counts: {
    anchors: 9,
    storylets: 16,
    scenes: 24,
    choices: 6,
    states: 22,
    endings: 6,
    artifacts: 96,
  },
  progress: [
    { label: "锚点", type: "anchor", total: 9, approved: 9, ratio: 1 },
    { label: "Canon", type: "canon", total: 1, approved: 1, ratio: 1 },
    { label: "分场", type: "scene", total: 24, approved: 11, ratio: 0.46 },
  ],
  gates: mockGates,
  pending_gates: mockGates.filter((gate) => gate.status === "pending").length,
  issues: mockValidation.issues,
  activity: [
    {
      actor: "usr_mo",
      role: "writer",
      decision: "approved",
      gate: "G5",
      artifact_id: "ART_scene",
      comment: "A02 婚房夜景对白修订通过",
      at: "2026-09-05T10:28:00Z",
    },
  ],
  recent_runs: mockRuns.slice(0, 4).map((run) => ({
    id: run.id,
    capability: run.capability,
    status: run.status,
    cost_units: run.cost_units,
    created_at: run.created_at,
  })),
  cost: mockCost,
  canon_gaps: [{ id: "GAP_01", topic: "武崧旧案细节", blocks: ["A03 正式对白"], status: "open" }],
};

/* ------------------------------------------------------------- 系统指标 */

export const mockSystemMetrics: SystemMetrics = {
  api_version: "v1",
  uptime_seconds: 318_402,
  http: {
    requests_total: 4_287,
    errors_total: 41,
    by_status: { "2xx": 4_182, "3xx": 64, "4xx": 35, "5xx": 6 },
    durations_ms: { count: 4_287, p50: 12.4, p95: 96.1, max: 1_024.8 },
  },
  business: {
    runs: {
      total: 212,
      // by_status 的键就是 `RunStatus` 的真实取值（后端按 DB 值动态聚合）。
      by_status: { WAITING_FOR_REVIEW: 174, APPROVED: 24, QUEUED: 5, RETRYABLE_FAILED: 9 },
    },
    cost: {
      ledger_entries: 212,
      prompt_tokens: 1_286_400,
      completion_tokens: 428_100,
      cost_units: 32_481,
    },
    artifacts: { total: 96, approved: 71, approval_rate: 0.74 },
    // 真实响应用小写决策名（后端 `review_records.decision`）：submitted / approved / rejected。
    reviews: { total: 118, by_decision: { submitted: 24, approved: 71, rejected: 23 } },
  },
};

/* -------------------------------------------- 健康与元信息（specs/14 §4.1） */

export const mockHealth: HealthStatus = {
  status: "ok",
  env: "development",
  name: "未必 Otherwise · 互动影游生产 Agent",
};

export const mockReady: ReadyStatus = {
  ready: true,
  database: "ok",
  server_version: "PostgreSQL 16.15 (Homebrew) on aarch64-apple-darwin25.6.0",
  error: "",
  methodology: `${mockMethodology.name}@${mockMethodology.version}`,
};

/**
 * 能力目录 —— 与 `GET /capabilities` 同构。
 *
 * 离线路径下生成中心的能力选择器同样由它驱动：AC-1406 要求能力名来自后端而非硬编码，
 * 离线模式不能成为这条约束的缺口。
 */
export const mockCapabilities: CapabilityCatalog = {
  capabilities: [
    {
      id: "writer_assistant",
      label: "编剧助理",
      allowed_write: ["brief", "scene", "script", "theme"],
      forbidden_operations: ["直接修改 Canon", "发布正式稿", "修改关系合同与锁点"],
      required_gate: null,
    },
    {
      id: "structure_engineer",
      label: "结构工程师",
      allowed_write: ["anchor", "choice", "fixture", "relationship", "state", "storylet"],
      forbidden_operations: ["修改主题", "修改人物真相", "修改伦理边界"],
      required_gate: "G2",
    },
    {
      id: "actor",
      label: "角色演员",
      allowed_write: ["script"],
      forbidden_operations: ["创造未知事实", "修改状态真相", "替其他角色做决定"],
      required_gate: "G5",
    },
    {
      id: "playtester",
      label: "测试玩家",
      allowed_write: ["test_report"],
      forbidden_operations: ["修改任何正式内容", "修改 Canon", "修改状态真相"],
      required_gate: null,
    },
    {
      id: "orchestrator",
      label: "内容编排器",
      allowed_write: [],
      forbidden_operations: [
        "临场生成新 Canon",
        "生成重大剧情",
        "修改状态真相",
        "直接修改 Artifact",
      ],
      required_gate: "G4",
    },
    {
      id: "director",
      label: "全局导演",
      allowed_write: [], // 真只读（第二十八轮与后端同步：diagnosis 不产出结构 Artifact）
      forbidden_operations: ["运行时直接发布", "越过总编剧批准", "修改 Canon"],
      required_gate: "G6",
    },
  ],
  cost_table: {
    writer_assistant: 40,
    structure_engineer: 60,
    actor: 30,
    playtester: 20,
    orchestrator: 15,
    director: 25,
  },
  model_unit_cost: {
    "latent-narrative-default": 1,
    "latent-narrative-strong": 3,
    "latent-narrative-lite": 0.35,
    "frameseed-c2": 12,
    "videoflow-s": 32,
  },
  allowed_artifact_types: [
    "anchor",
    "brief",
    "canon",
    "character",
    "choice",
    "fixture",
    "relationship",
    "scene",
    "script",
    "state",
    "storylet",
    "test_report",
    "theme",
  ],
  checked_at: "2026-09-14T06:15:58Z",
};

/* ---------------------------------------------- 方法论列表（specs/14 §4.10） */

/**
 * `GET /methodologies` 的离线对应物。
 *
 * `registered` 如实为**空**：黄金夹具的库里没有发布过其它版本包，
 * 所以「升级」在离线演示里同样只能选当前版本——这不是 mock 的缺陷，
 * 而是与真实数据一致的呈现。
 */
export const mockMethodologyList: MethodologyList = {
  current: {
    name: mockMethodology.name,
    version: mockMethodology.version,
    ref: `${mockMethodology.name}@${mockMethodology.version}`,
  },
  registered: [],
};

/* ------------------------------------ 六维变更影响预览（specs/14 §4.3） */

/** `POST /projects/{id}/profile/impact` 的离线对应物（字段按 F-4 的真机响应）。 */
export const mockProfileImpact: ProfileImpact = {
  changed_fields: ["dramatic_genre.primary", "platform_profile"],
  stages: ["S1", "S3", "S4", "S5", "S6", "S7"],
  artifact_types: ["anchor", "choice", "scene", "script", "state", "test_report", "theme"],
  full_rebuild: false,
  notes: [
    "dramatic_genre.primary：戏剧类型决定节奏、信息规则与对白检查。",
    "platform_profile：平台影响集长、选择密度、回顾与素材预算。",
  ],
  recommendation: "仅受影响的 Artifact 需标记为 STALE，可局部重跑",
};

/* -------------------------------------------------------- 模拟历史比较 mock */

/**
 * 离线模式下模拟历史比较（F-7）。
 *
 * 用 `mockSimulation`（最新的成功模拟）与 `mockSimulations[1]`（较早的带告警模拟）
 * 构造出一个有实质差异的对比数据。
 */
export function mockSimulationCompare(baseId: string, targetId: string): SimulationCompare {
  const earlier = mockSimulations[1]; // SIM_JINLIAN_EARLIER — 带 issues
  const latest = mockSimulations[0]; // SIM_000918 — 无 issues

  // 对齐 base/target：让较早的那次做 base，新的做 target
  const base = baseId === earlier.id ? earlier : latest;
  const target = targetId === latest.id ? latest : earlier;

  const baseCov = base.coverage;
  const targetCov = target.coverage;

  const coverageDiff: Record<string, CoverageDiffItem> = {};
  // 数值字段
  const numericKeys = [
    "storylet_coverage",
    "choice_coverage",
    "endings_total",
    "endings_reachable_ratio",
    "paths",
    "failed_paths",
    "dead_ends",
    "romance_free_paths",
  ] as const;
  for (const key of numericKeys) {
    const b = (baseCov as any)[key] ?? 0;
    const t = (targetCov as any)[key] ?? 0;
    coverageDiff[key] = { base: b, target: t, change: t - b };
  }
  if (baseCov.endings_reached && targetCov.endings_reached) {
    coverageDiff["endings_reached"] = {
      base: (baseCov.endings_reached as string[]).length,
      target: (targetCov.endings_reached as string[]).length,
      change:
        (targetCov.endings_reached as string[]).length -
        (baseCov.endings_reached as string[]).length,
    };
  }
  if (baseCov.visited && targetCov.visited) {
    coverageDiff["visited"] = {
      base: (baseCov.visited as string[]).length,
      target: (targetCov.visited as string[]).length,
      change: (targetCov.visited as string[]).length - (baseCov.visited as string[]).length,
    };
  }

  const pathDiffs: PathDiff[] = (mockSimulation.paths ?? []).map((p) => ({
    player_type: p.player_type,
    base_ending: p.ending_id,
    target_ending: p.ending_id,
    same_ending: true,
    base_steps: p.steps.length,
    target_steps: p.steps.length,
    base_failures: p.failures,
    target_failures: [],
    failures_unchanged: 0,
    failures_resolved: p.failures.length,
    failures_introduced: 0,
  }));

  return {
    base_id: base.id,
    target_id: target.id,
    coverage_diff: coverageDiff,
    path_diffs: pathDiffs,
    issues_diff: {
      unchanged: [],
      resolved: [],
      introduced:
        latest.issues_count === 0
          ? []
          : [{ rule_id: "R15", severity: "error", message: "模拟告警从有到无" }],
    },
    methodology_changed: base.methodology_version !== target.methodology_version,
    profile_changed: base.profile_version !== target.profile_version,
  };
}

/* ------------------------------------ 观众侧：分析与操作记录（specs/15 §3–§4） */

/**
 * `GET /projects/{id}/audience/analytics` 的离线对应物。
 *
 * ⚠️ 这里**不重算**兴趣分：`interest_score` 是后端按 HEU-012 的权重算好的读数，
 * 离线副本**照抄**（RULES §29 的取向——同一份口径不写两遍）。
 * 分母为 0 的行（`SL_NEW`）四个读数都是 `null`，界面必须显示「—」。
 */
const MOCK_AUDIENCE_ROWS: Record<"storylet" | "story" | "project", AudienceMetricRow[]> = {
  storylet: [
    {
      key: "SL_A01",
      impressions: 120,
      clicks: 48,
      selects: 36,
      click_rate: 0.4,
      select_rate: 0.3,
      avg_dwell_ms: 8200,
      interest_score: 0.324,
    },
    {
      key: "SL_A02",
      impressions: 80,
      clicks: 20,
      selects: 14,
      click_rate: 0.25,
      select_rate: 0.175,
      avg_dwell_ms: 5200,
      interest_score: 0.204,
    },
    {
      key: "SL_LOCK",
      impressions: 40,
      clicks: 4,
      selects: 2,
      click_rate: 0.1,
      select_rate: 0.05,
      avg_dwell_ms: 2100,
      interest_score: 0.082,
    },
    {
      key: "SL_NEW",
      impressions: 0,
      clicks: 3,
      selects: 0,
      click_rate: null,
      select_rate: null,
      avg_dwell_ms: null,
      interest_score: null,
    },
  ],
  story: [
    // `group_by=story` **≡** `group_by=project`（无"故事"层，`specs/15` §10 第 6 条 · `ADR-024` 同源纪律）：
    // 故这一行的 key 是**项目 id**，与下面的 `project` 维度逐字相同。
    {
      key: DEFAULT_PROJECT_ID,
      impressions: 240,
      clicks: 72,
      selects: 52,
      click_rate: 0.3,
      select_rate: 0.216667,
      avg_dwell_ms: 7000,
      interest_score: 0.26,
    },
  ],
  project: [
    {
      key: DEFAULT_PROJECT_ID,
      impressions: 240,
      clicks: 72,
      selects: 52,
      click_rate: 0.3,
      select_rate: 0.216667,
      avg_dwell_ms: 7000,
      interest_score: 0.26,
    },
  ],
};

export function mockAudienceAnalytics(
  groupBy: "storylet" | "story" | "project",
): AudienceAnalytics {
  return { group_by: groupBy, rows: MOCK_AUDIENCE_ROWS[groupBy] };
}

/**
 * 离线逐幕播放器：**照抄后端语义**（`specs/15` §3）用黄金夹具走一遍。
 *
 * 内容只读 `storylets.json` / `choices.json`；推进用 `next[0]`（与后端同一处**待决**简化，
 * `specs/15` §10）。这里**不写任何故事内容**，全部来自已同步的基线数据。
 */
interface RawStorylet {
  id: string;
  title: string;
  scene_task: string;
  dramatic_function: string;
  choice_id: string | null;
  next?: string[];
}

interface RawChoice {
  id: string;
  options?: {
    id: string;
    action: string;
    motivation: string;
    immediate_gain: string;
    immediate_cost: string;
  }[];
}

const _storylets = (goldenStorylets as unknown as { storylets: RawStorylet[] }).storylets;
const _startKey = (goldenStorylets as unknown as { start: string }).start;
const _choices = (goldenChoices as unknown as { choices: RawChoice[] }).choices;

function _storyletById(key: string | null): RawStorylet | null {
  return _storylets.find((item) => item.id === key) ?? null;
}

function _choicesFor(storylet: RawStorylet | null) {
  const choice = storylet?.choice_id ? _choices.find((c) => c.id === storylet.choice_id) : null;
  return (choice?.options ?? []).map((option) => ({
    key: option.id,
    action: option.action,
    motivation: option.motivation,
    immediate_gain: option.immediate_gain,
    immediate_cost: option.immediate_cost,
  }));
}

/** 离线的一次游玩状态；`currentKey === null` 即已结束。 */
export interface MockPlayState {
  id: string;
  project_id: string;
  /**
   * 三种状态，**与后端 `play_service.PLAYTHROUGH_STATUSES` 逐字一致**。
   *
   * ⚠️ 此前这里只写了两值（`IN_PROGRESS` / `FINISHED`）——而 `Playthrough.status`
   * 有三值。那不是"多余的严格"，而是**与后端枚举不一致的活缺陷**：`ABANDONED`
   * 曾在后端有定义、在契约里有、在前端类型里有，**唯独这里造不出来**
   * ⇒ 离线态永远演不出放弃，与"该状态没有写入者"是同一族缺陷的两个面
   * （一个在服务端、一个在替身）。这类不一致**不会让守卫变红**，是本轮
   * 补放弃功能时才被 `tsc` 撞出来的。
   */
  status: "IN_PROGRESS" | "FINISHED" | "ABANDONED";
  current_key: string | null;
  steps_taken: number;
}

export function mockPlaythroughStart(projectId: string): {
  state: MockPlayState;
  view: Playthrough;
} {
  const state: MockPlayState = {
    id: "PLAY_OFFLINE_1",
    project_id: projectId,
    status: "IN_PROGRESS",
    current_key: _startKey,
    steps_taken: 0,
  };
  return { state, view: mockPlaythroughView(state) };
}

export function mockPlaythroughStep(
  state: MockPlayState,
  chosenKey: string | null,
): { state: MockPlayState; view: Playthrough } {
  const current = _storyletById(state.current_key);
  const nextKey = current?.next?.find((key) => _storyletById(key)) ?? null;
  const advanced: MockPlayState = {
    ...state,
    current_key: nextKey,
    status: nextKey ? "IN_PROGRESS" : "FINISHED",
    steps_taken: state.steps_taken + 1,
  };
  void chosenKey; // 离线不落库；这里只需推进（与后端"只读推进"同形）。
  return { state: advanced, view: mockPlaythroughView(advanced) };
}

/**
 * 主动放弃一次游玩（`specs/15` §3 · §10 第 10 条 · `ADR` 产品方 2026-10-06 裁决）。
 *
 * 与后端同形：只**标记**状态、**不丢**已走的步数（`steps_taken` 原样保留），
 * 收尾形状与 `FINISHED` 一致（`current_key` 清空 ⇒ 视图不再呈现幕）。
 */
export function mockPlaythroughAbandon(state: MockPlayState): {
  state: MockPlayState;
  view: Playthrough;
} {
  const abandoned: MockPlayState = {
    ...state,
    current_key: null,
    status: "ABANDONED",
  };
  return { state: abandoned, view: mockPlaythroughView(abandoned) };
}

/** 把离线游玩状态渲染成与 `PlaythroughOut` 同构的视图。 */
export function mockPlaythroughView(state: MockPlayState): Playthrough {
  const storylet = state.status === "IN_PROGRESS" ? _storyletById(state.current_key) : null;
  return {
    id: state.id,
    project_id: state.project_id,
    status: state.status,
    storylet: storylet
      ? {
          key: storylet.id,
          title: storylet.title,
          scene_task: storylet.scene_task,
          dramatic_function: storylet.dramatic_function,
        }
      : null,
    choices: _choicesFor(storylet),
    steps_taken: state.steps_taken,
  };
}

/** `GET /audience/playthroughs/{id}` 的离线对应物（续玩恢复）。离线只有一部作品、一幕现状。 */
export function mockResumePlaythrough(playthroughId: string): Playthrough {
  void playthroughId; // 离线不区分会话；真实路径由后端按 id 返回当前幕。
  return mockPlaythroughStart(DEFAULT_PROJECT_ID).view;
}

/** `GET /projects/{id}/audience/playthroughs` 的离线对应物（编辑端操作记录）。 */
export const mockAudiencePlaythroughs: AudiencePlaythroughs = {
  project_id: DEFAULT_PROJECT_ID,
  items: [
    {
      id: "PLAY_0002",
      project_id: DEFAULT_PROJECT_ID,
      user_id: "USR_0002",
      status: "IN_PROGRESS",
      started_at: "2026-10-04T09:12:00Z",
      finished_at: null,
      steps: [
        { seq: 1, storylet_key: "A01", chosen_key: null, at: "2026-10-04T09:12:04Z" },
        { seq: 2, storylet_key: "A02", chosen_key: "OPT_B", at: "2026-10-04T09:12:31Z" },
      ],
    },
    {
      id: "PLAY_0001",
      project_id: DEFAULT_PROJECT_ID,
      user_id: "USR_0001",
      status: "FINISHED",
      started_at: "2026-10-03T20:41:00Z",
      finished_at: "2026-10-03T20:48:00Z",
      steps: [
        { seq: 1, storylet_key: "A01", chosen_key: null, at: "2026-10-03T20:41:05Z" },
        { seq: 2, storylet_key: "A02", chosen_key: "OPT_A", at: "2026-10-03T20:42:10Z" },
        { seq: 3, storylet_key: "A03", chosen_key: "OPT_C", at: "2026-10-03T20:47:58Z" },
      ],
    },
  ],
};

/* ---------------------------------------- 共创社区：脑洞（specs/15 §11） */

/**
 * `GET /community/ideas` 的离线对应物。
 *
 * 内容全部来自已同步的基线（`data/baseline.json` 的 `ideas`），**不在此手写故事内容**
 * （specs/07 §7）。`liked_by_me` 初始为假、`created_at` 为空——它们是"当前身份"与
 * "落库时刻"，离线演示没有这两样，如实留空而不是编造。
 */
export const mockCommunityIdeas: CommunityIdea[] = (
  baseline.ideas as unknown as {
    id: string;
    source: string;
    ip: string;
    title: string;
    text: string;
    heat: number;
    adopted: boolean;
  }[]
).map((idea) => ({
  id: idea.id,
  source: idea.source,
  ip: idea.ip,
  title: idea.title,
  text: idea.text,
  heat: idea.heat,
  adopted: idea.adopted,
  liked_by_me: false,
  created_at: null,
}));
